// GELÉ le 04/10/2026. Base saine = commit 338328aa.
// Interdit d'y retoucher sans un nouveau gel explicite de Hiti.
// Bugs déjà réglés, ne pas les rouvrir :
//
// 1. iPhone iOS 17.1+ (Safari et Chrome, même moteur) passe par hls.js dès que Hls.isSupported() est vrai. Pas de prefersNativeHls() pour choisir le lecteur. Pas de video.src sur un m3u8 dans ce cas. Le quatrième lecteur natif (load sans hls.js) faisait une carte noire : il reste interdit.
// 2. Voisines : hors économie de données, pas de stopLoad sur la carte visible. L'arrêt vers 3 s ne concerne que l'avance (N+2 et N-2) et l'économie de données. Les flux d'avance sont dans aheadStreams, pas dans slots. maxSources() reste 3.
// 3. iOS coupe le chargement hors écran (endstreaming). keepBuffering rappelle resumeBuffering tant que la carte n'est pas active avec le chargement ouvert. startPlayback rappelle resumeBuffering si bufferingEnabled est faux.
// 4. Ne pas passer de muet à sonore avant play(). Démute dans le then() de play(), une fois la lecture partie. Sinon iOS relance le décodeur : micro-pause.
// 5. Boucle : el.loop reste. En plus, wrapLoopBeforeEnd recadre currentTime à 0 s'il reste moins de 0,25 s ET que le début est dans le tampon. Une fois par tour. Pas de saut si le début n'est pas tamponné. C'est la seule écriture de currentTime hors du seek du membre.
// 6. Reprise : si le tampon ne couvre pas currentTime, startLoad(currentTime) avant play().
// 7. Palier admin (video_playback_start_quality) lu avant l'ouverture du fil. Hauteur fixe : testBandwidth false, capLevelToPlayerSize false, autoStartLoad false. Pas de nextLevel avant startLoad (startLoad remet le niveau à -1).
// 8. Vidéos verticales 9:16. Bunny : 360p = 360x640, 720p = 720x1280. Le palier se mesure avec Math.min(width, height), jamais la hauteur seule. Sinon 640 est plus près de 720 que 1280, et le départ reste en 360.
// 9. minAutoBitrate = débit du palier choisi. L'ABR peut monter, pas descendre en dessous. Pas de loadLevel ni currentLevel.
// 10. watchStuck ne détruit pas une source qui a hls.js. La bulle son lit le déverrouillage de session, pas muted.
// GEL BÊTA 07/10 — docs/GEL_BETA.md. Porte ouverte aux retours Hiti, sans toucher la logique lecteur.
import Hls from "hls.js";
import type { HlsConfig } from "hls.js";
import { getStartQuality, targetHeightOf } from "./playbackQuality";

/**
 * Configuration hls.js partagée par les éléments du pool.
 * Buffer court : on laisse de la bande passante au préchargement de la
 * diapositive voisine. Le palier de départ est un réglage admin
 * (`app_settings.video_playback_start_quality`), jamais une valeur en dur.
 */
const BASE_CONFIG: Partial<HlsConfig> = {
  startFragPrefetch: true,
  abrBandWidthFactor: 0.9,
  abrBandWidthUpFactor: 0.7,
  maxBufferLength: 16,
  maxMaxBufferLength: 24,
  // Tampon arrière long : sur une vidéo courte de feed, cela garde le début en
  // mémoire, donc la boucle ne re-télécharge pas le fragment 0.
  backBufferLength: 60,
  capLevelToPlayerSize: true,
  enableWorker: true,
  lowLatencyMode: false,
};

export function buildHlsConfig(): Partial<HlsConfig> {
  const quality = getStartQuality();

  if (quality === "lowest") {
    return {
      ...BASE_CONFIG,
      startLevel: 0,
      testBandwidth: false,
      abrEwmaDefaultEstimate: 500_000,
    };
  }

  // Hauteur fixe : aucun test de débit ni plafond à la taille du lecteur, et
  // chargement lancé seulement une fois le palier choisi (bindStartQuality).
  // L'estimation de départ suit la hauteur pour que l'ABR tombe déjà dessus.
  const height = targetHeightOf(quality);
  if (height !== null) {
    return {
      ...BASE_CONFIG,
      startLevel: -1,
      testBandwidth: false,
      capLevelToPlayerSize: false,
      autoStartLoad: false,
      abrEwmaDefaultEstimate: estimateForHeight(height),
    };
  }

  return { ...BASE_CONFIG, startLevel: -1, testBandwidth: true };
}

/**
 * Débit de départ supposé (bits/s), volontairement au-dessus du débit annoncé
 * par le CDN : même rabattu par abrBandWidthFactor (0,9), l'ABR doit rester
 * sur la hauteur demandée dès le premier morceau.
 */
function estimateForHeight(height: number): number {
  if (height <= 360) return 2_000_000;
  if (height <= 480) return 4_000_000;
  if (height <= 720) return 8_000_000;
  return 14_000_000;
}

/**
 * Applique le palier de départ demandé quand il s'agit d'une hauteur fixe.
 * Plancher ABR : `minAutoBitrate` est posé au débit du palier choisi, donc
 * l'ABR ne peut plus descendre en dessous, seulement monter. On ne touche
 * jamais à `nextLevel` : dans hls.js 1.7, `startLoad()` remet le niveau à -1
 * et annulerait un chargement lancé via `nextLevel`.
 */
export function bindStartQuality(hls: Hls): void {
  const height = targetHeightOf();
  if (height === null) return;

  hls.once(Hls.Events.MANIFEST_PARSED, () => {
    const levels = hls.levels || [];
    // Vidéos verticales : 720x1280 = palier 720. On mesure le plus petit côté.
    const chosen = closestIndex(
      levels.map((level) => {
        const w = level.width || 0;
        const h = level.height || 0;
        return w && h ? Math.min(w, h) : w || h;
      }),
      height,
    );
    if (chosen >= 0) {
      // Débit du palier choisi : référence du plancher de l'ABR.
      const level = levels[chosen];
      const bitrate = level.maxBitrate || level.bitrate || 0;
      if (bitrate > 0) {
        hls.config.minAutoBitrate = bitrate;
      }
      hls.startLevel = chosen;
    }
    hls.startLoad();
  });
}

/** Index de la hauteur la plus proche de la cible, -1 si la liste est vide. */
export function closestIndex(heights: number[], target: number): number {
  let best = -1;
  let bestGap = Number.POSITIVE_INFINITY;
  heights.forEach((h, index) => {
    const gap = Math.abs(h - target);
    if (gap < bestGap) {
      bestGap = gap;
      best = index;
    }
  });
  return best;
}

/**
 * WebKit (Safari macOS/iOS, Chrome et Firefox iOS) : lecture HLS native.
 * hls.js y est contre-productif (MSE limité, décodeurs concurrents).
 */
export function prefersNativeHls(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ se présente comme un Mac tactile.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return isIOS || isSafari;
}
